/**
 * ACHICAR CON REINYECCIÓN PROMEDIADA: cuánto estable aportar para que un rango más estrecho te
 * saque con tu capital MÁS lo que aportas.
 *
 * La piscina está bajo el agua: vale D y debe T. Se cierra y se reabre con D + A en un rango
 * [a, b] con el precio cerca del borde del estable (solo un `s` ~3% queda en estable). El
 * contrato (`recenterTight`, lógica nueva) COMPRA el activo con el aporte, así que hay más unidades
 * y el borde de salida queda más cerca. Al tocarlo, todo es estable y vale (D + A)·R, con
 *
 *      R = valor por unidad de L en el borde de salida / valor por unidad de L hoy
 *
 * y se pide (D + A)·R = T + A  →  A = (T − D·R)/(R − 1). Es la fórmula de "Reducir rango con
 * reinyección promediada"; aquí con las mismas piezas que usa el contrato para que el número que
 * se firma sea el que pasa:
 *
 *   · el rango se arma sobre el TWAP (`_normalRange`: floor(twap − below), floor(twap + above)),
 *     no sobre el spot, y los dos semianchos van en múltiplos del tickSpacing;
 *   · la compra paga la comisión de la pool sobre lo que se convierte;
 *   · la reserva, la bolsa de gas y la ganancia apartada no se despliegan (ni cuentan aquí).
 *
 * Se trabaja con raíces CRUDAS (sin decimales): R y la fracción en estable son cocientes y los
 * decimales se cancelan. D, T y A van en unidades humanas del estable.
 */

const raiz = (t: number) => Math.pow(1.0001, t / 2);
const suelo = (t: number, sp: number) => Math.floor(t / sp) * sp;

/** Margen a favor del dueño: se apunta a salir con un 0,2% más del objetivo. */
export const MARGEN = 0.002;
/** Ancho mínimo del rango nuevo (la herramienta de referencia usa el mismo). */
export const ANCHO_MIN = 0.05;

export type Entrada = {
  /** el estable es token0 (pares tipo USDG/AAPL): se recupera cuando el precio BAJA */
  refEsToken0: boolean;
  /** raíz del precio de la pool (cruda, slot0/2^96) y su tick */
  sqrtP: number;
  tick: number;
  /** tick medio de la ventana TWAP del vault: el centro sobre el que el contrato arma el rango */
  twap: number;
  spacing: number;
  /** comisión de la pool, fracción (0,003 = 0,3%) */
  fee: number;
  /** valor desplegable hoy (sin reserva/gas/ganancia) y su parte en estable, humano */
  D: number;
  estable: number;
  /** objetivo a recuperar con lo desplegable (costBasisRef − reserva), humano */
  T: number;
};

export type Plan = {
  below: number;
  above: number;
  lo: number;
  hi: number;
  /** aporte de estable, humano */
  aporte: number;
  /** con lo que se sale al tocar el borde (todo en estable), humano */
  salida: number;
  /** cuánto tiene que moverse el precio hasta el borde de salida, en % (del activo, siempre >0) */
  subida: number;
  /** cuánto cabe que caiga el activo antes de salir por el otro lado, en % */
  colchon: number;
  /** fracción en estable con la que abre */
  fracEstable: number;
  /** amplitud del rango, (b−a)/a en precio */
  ancho: number;
};

/** R, fracción en estable y validez para un rango ya en ticks. */
function forma(e: Entrada, lo: number, hi: number) {
  if (!(lo <= e.tick && e.tick < hi)) return null; // el contrato solo compra con el precio dentro
  const S = e.sqrtP, a = raiz(lo), b = raiz(hi);
  if (!(a < S && S < b)) return null;
  const x = 1 / S - 1 / b; // token0 por unidad de L hoy
  const y = S - a;         // token1 por unidad de L hoy
  if (e.refEsToken0) {
    const valor = x + y / (S * S);
    return { R: (1 / a - 1 / b) / valor, frac: x / valor };
  }
  const valor = y + x * S * S;
  return { R: (b - a) / valor, frac: y / valor };
}

/** El plan para unos semianchos concretos: el aporte justo para salir con objetivo + aporte. */
export function planPara(e: Entrada, below: number, above: number): Plan | null {
  const lo = suelo(e.twap - below, e.spacing);
  let hi = suelo(e.twap + above, e.spacing);
  if (hi <= lo) hi = lo + e.spacing;
  const f = forma(e, lo, hi);
  if (!f || f.R <= 1) return null;
  const { R, frac } = f;
  const m = 1 + MARGEN;

  // Se compra X = estable + A − frac·(D + A) (lo que sobra de estable para la proporción del
  // rango) y la pool se queda fee·X. Valor desplegado = D + A − fee·X. Despejando A de
  // (D + A − fee·X)·R = (T + A)·m sale lineal:
  const den = R * (1 - e.fee + e.fee * frac) - m;
  if (den <= 0) return null; // ningún aporte alcanza: el rango no sube lo bastante
  let A = (e.T * m - R * (e.D - e.fee * e.estable + e.fee * frac * e.D)) / den;
  // Sin estable suficiente para la proporción, el activo no cabe entero y se queda fuera: el
  // aporte tiene que cubrir al menos eso (con ello X ≥ 0 y la fórmula de arriba vale).
  const minProporcion = (frac * e.D - e.estable) / (1 - frac);
  A = Math.max(0, A, minProporcion);
  const X = Math.max(0, e.estable + A - frac * (e.D + A));
  const salida = (e.D + A - e.fee * X) * R;

  const precio = (t: number) => Math.pow(1.0001, t);
  const hoy = e.sqrtP * e.sqrtP;
  const subida = e.refEsToken0 ? (hoy / precio(lo) - 1) * 100 : (precio(hi) / hoy - 1) * 100;
  const colchon = e.refEsToken0 ? (1 - hoy / precio(hi)) * 100 : (1 - precio(lo) / hoy) * 100;
  return {
    below, above, lo, hi, aporte: A, salida, subida, colchon, fracEstable: frac,
    ancho: precio(hi - lo) - 1,
  };
}

/** Semianchos para una amplitud total (fracción) abriendo con ~`s` en estable. */
export function planAncho(e: Entrada, ancho: number, s: number): Plan | null {
  const sp = e.spacing;
  const total = Math.max(2 * sp, Math.round(Math.log(1 + ancho) / Math.log(1.0001) / sp) * sp);
  // El lado CERCANO es el del estable: abajo si el estable es token1, arriba si es token0. Se toma
  // el más corto que deja al menos `s` en estable.
  //
  // Y SOLO lados cercanos que el contrato reconozca como "achicar promediado": compra el activo y
  // arma la banda exacta a capital (+ aporte) únicamente si `(centro − suelo)·4 < ancho`. El suelo
  // se redondea hacia abajo al spacing, así que el lado cercano REAL va de `cerca` a `cerca + sp`:
  // con `(cerca + sp)·4 ≤ total` la condición se cumple siempre. Sin esto, en pools de spacing
  // grande (GME, 200) la app proponía cerca = sp, el contrato no compraba ni ajustaba la banda y
  // el rango no salía con lo prometido (vault 0xf735…, 2026-10-29).
  for (let cerca = sp; (cerca + sp) * 4 <= total; cerca += sp) {
    const lejos = total - cerca;
    const [below, above] = e.refEsToken0 ? [lejos, cerca] : [cerca, lejos];
    const p = planPara(e, below, above);
    if (p && p.fracEstable >= s) return p;
  }
  return null;
}

/** El rango más estrecho que se puede pagar con `aporte` (o null si no hay). */
export function planAporte(e: Entrada, aporte: number, s: number, anchoMax: number): Plan | null {
  let mejor: Plan | null = null;
  // Del más ancho al más estrecho, en pasos de un spacing de amplitud total.
  const sp = e.spacing;
  const maxTicks = Math.round(Math.log(1 + anchoMax) / Math.log(1.0001));
  const minTicks = Math.round(Math.log(1 + ANCHO_MIN) / Math.log(1.0001));
  for (let t = Math.ceil(maxTicks / sp) * sp; t >= minTicks; t -= sp) {
    const p = planAncho(e, Math.pow(1.0001, t) - 1, s);
    if (!p) continue;
    if (p.aporte <= aporte) mejor = p;
    else break; // más estrecho pide aún más: no hay que seguir
  }
  return mejor;
}

/** TWAP como lo calcula el contrato: media de tickCumulatives redondeada hacia −∞. */
export function twapDe(cum0: bigint, cum1: bigint, ventana: number): number {
  const delta = cum1 - cum0;
  const w = BigInt(ventana);
  let avg = delta / w;
  if (delta < 0n && delta % w !== 0n) avg -= 1n;
  return Number(avg);
}
